import { Skeleton, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLPaper } from "OLPaper";
import { hasAnyAccessToResource } from "api/general";
import { useSegments } from "api/segments";
import { ReactComponent as File05Icon } from "components/Icons/file-05.svg";
import { ReactComponent as Rows01Icon } from "components/Icons/rows-01.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { useState } from "react";
import { BackendMemberModel } from "types/members/main";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    item: {
      background: theme.gray50,
      border: `1px solid ${theme.gray200}`,
      borderRadius: "4px",
      maxWidth: 400,
      overflow: "hidden",
      padding: theme.spacing(2, 4),
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    more: {
      background: theme.primary800,
      borderRadius: "4px",
      color: theme.white50,
      cursor: "pointer",
      maxWidth: "fit-content",
      padding: theme.spacing(2, 4),
      whiteSpace: "nowrap",
    },
    title: {
      alignItems: "center",
      color: theme.gray500,
      display: "flex",
      fontSize: 14,
      fontWeight: 400,
      gap: theme.spacing(2),
      lineHeight: "20px",
      padding: theme.spacing(6, 8, 0, 8),
    },
    icon: {
      color: theme.gray500,
      height: "16px",
      width: "16px",
    },
    itemsContainer: {
      padding: theme.spacing(4, 8, 6, 8),
      alignItems: "start",
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(2),
      maxWidth: "100%",
    },
    divider: {
      borderBottom: `1px solid ${theme.gray200}`,
      width: "100%",
    },
    customAttrContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(4),
    },
  }),
);

interface Props {
  membersData?: BackendMemberModel;
}

export const Item = ({
  className,
  value,
}: {
  className?: string;
  value: string;
}) => {
  const { observeElement, isOverflown } = useIsOverflown();
  const classes = useStyles();

  return (
    <Tooltip text={value} disableHoverListener={!isOverflown}>
      <div
        className={`${classes.item} ${className ? className : ""}`}
        ref={observeElement}
      >
        {value}
      </div>
    </Tooltip>
  );
};
export const More = ({
  value,
  onClick,
}: {
  value: number;
  onClick: () => void;
}) => {
  const classes = useStyles();
  const { message } = useMessage();

  return (
    <div className={classes.more} onClick={onClick}>
      {message({ id: "plusMore" }, { more: value })}
    </div>
  );
};

export const SegmentItem = ({
  className,
  id,
}: {
  className?: string;
  id: string;
}) => {
  const { useSegmentData } = useSegments();
  const { data: segmentData } = useSegmentData(id);
  return (
    <>
      {segmentData ? (
        <Item className={className} value={segmentData?.name} />
      ) : (
        <Skeleton height={33} width={70} />
      )}
    </>
  );
};

export const SegmentsAndCustomAttributes = ({ membersData }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const [isSegmentsModalOpen, setIsSegmentsModalOpen] = useState(false);
  const [isCustomAttributesModalOpen, setIsCustomAttributesModalOpen] =
    useState(false);

  const visibleItems = 10;
  const hasAnySegmentPermission = hasAnyAccessToResource("SEGMENT");

  return (
    <OLPaper noPadding>
      {hasAnySegmentPermission && (
        <>
          <Typography className={classes.title}>
            <Rows01Icon className={classes.icon} />
            {message({ id: "segments" })}
          </Typography>
          {membersData ? (
            <div className={classes.itemsContainer}>
              {membersData.segments.length ? (
                <>
                  {membersData.segments
                    ?.slice(0, visibleItems)
                    .map((item: string) => (
                      <SegmentItem id={item} key={item} />
                    ))}
                  {membersData.segments.length > visibleItems && (
                    <More
                      onClick={() => setIsSegmentsModalOpen(true)}
                      value={membersData.segments.length - visibleItems}
                    />
                  )}
                </>
              ) : (
                "-"
              )}
            </div>
          ) : (
            <Skeleton height={100} />
          )}
        </>
      )}
      <div className={classes.divider} />
      <Typography className={classes.title}>
        <File05Icon className={classes.icon} />
        {message({ id: "customAttributes" })}
      </Typography>
      {membersData ? (
        <div className={classes.itemsContainer}>
          {membersData.labels.length ? (
            <>
              {membersData.labels
                ?.slice(0, visibleItems)
                .map((item) => (
                  <Item value={`${item.key}: ${item.value}`} key={item.key} />
                ))}
              {membersData.labels.length > visibleItems && (
                <More
                  onClick={() => setIsCustomAttributesModalOpen(true)}
                  value={membersData.labels.length - visibleItems}
                />
              )}
            </>
          ) : (
            "-"
          )}
        </div>
      ) : (
        <Skeleton height={100} />
      )}
      <OLModal
        header={message({ id: "segments" })}
        modalWidth={547}
        onClose={() => setIsSegmentsModalOpen(false)}
        open={isSegmentsModalOpen}
        submitAction={() => setIsSegmentsModalOpen(false)}
        submitText={message({ id: "close" })}
      >
        <div className={classes.customAttrContainer}>
          {membersData?.segments?.map((attribute, index) => (
            <SegmentItem id={attribute} key={index} />
          ))}
        </div>
      </OLModal>
      <OLModal
        header={message({ id: "customAttributes" })}
        modalWidth={547}
        onClose={() => setIsCustomAttributesModalOpen(false)}
        open={isCustomAttributesModalOpen}
        submitAction={() => setIsCustomAttributesModalOpen(false)}
        submitText={message({ id: "close" })}
      >
        <div className={classes.customAttrContainer}>
          {membersData?.labels?.map((attribute) => (
            <Item
              key={attribute.key}
              value={`${attribute.key}: ${attribute.value}`}
            />
          ))}
        </div>
      </OLModal>
    </OLPaper>
  );
};
